import { createContext, useContext, useState, useEffect } from "react";

const ThemeContext = createContext();

// Logo URLs — served from the app's public folder so they're bundled with the deployment
// and don't depend on any external asset host (avoids CSP/mixed-content/link-rot issues).
const LOGOS = {
  dark: {
    horizontal: "/logos/dark-horizontal.png",
    icon: "/logos/dark-icon.png",
    stacked: "/logos/dark-stacked.png",
  },
  light: {
    horizontal: "/logos/light-horizontal.png",
    icon: "/logos/light-icon.png",
    stacked: "/logos/light-stacked.png",
  },
};

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("rhz-theme") || "light";
    }
    return "light";
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "light") {
      root.classList.add("light");
      root.classList.remove("dark");
    } else {
      root.classList.add("dark");
      root.classList.remove("light");
    }
    localStorage.setItem("rhz-theme", theme);
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));
  const isDark = theme === "dark";
  // In light mode, use same white logos but invert with CSS
  // Dark logos are white on black - invert(1) makes them dark on white
  const logos = LOGOS.dark;
  const logoStyle = isDark ? {} : { filter: "invert(1)" };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, isDark, logos, logoStyle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
